import { add, format } from 'date-fns';
import { formatInTimeZone, zonedTimeToUtc } from 'date-fns-tz';

import {
  ICalendarEvent,
  ICalendarEventOrganizer,
  INormalizedCalendarEvent,
  IGoogle,
  IOutlook,
  IYahoo,
} from './interfaces';

const TimeFormats = {
  dateTimeWithOffset: "yyyy-MM-dd'T'hh:mm:ss':00Z'",
  dateTimeUTC: "yyyyMMdd'T'HHmmss",
  allDay: 'yyyyMMdd',
};

function formatTimes(
  { allDayEvent, startUtc, endUtc }: INormalizedCalendarEvent,
  dateTimeFormat: keyof typeof TimeFormats
): { start: string; end: string } {
  const formatPattern = TimeFormats[dateTimeFormat];

  return { start: format(new Date(startUtc), formatPattern), end: format(new Date(endUtc), formatPattern) };
}

function formatForGoogle(startUtc: string, endUtc: string, allDay: boolean) {
  const formatPattern = allDay ? 'yyyyMMdd' : "yyyyMMdd'T'HHmmss'Z'";

  return {
    start: formatInTimeZone(new Date(startUtc), 'UTC', formatPattern),
    end: formatInTimeZone(new Date(endUtc), 'UTC', formatPattern),
  };
}

export const eventify = (event: ICalendarEvent): INormalizedCalendarEvent => {
  const { startUtc, endUtc: endUTC, duration, timeZone, ...rest } = event;

  const endUtc = endUTC
    ? endUTC
    : (() => {
        if (event.allDayEvent) {
          return add(new Date(startUtc), { days: 1 }).toISOString();
        }

        if (duration && duration.length == 2) {
          const value = Number(duration[0]);
          const unit = duration[1];

          return add(new Date(startUtc), { [unit]: value }).toISOString();
        }

        return zonedTimeToUtc(new Date(), timeZone).toISOString();
      })();

  return {
    ...rest,
    startUtc,
    endUtc,
    timeZone,
  };
};

export const google = (calendarEvent: ICalendarEvent): string => {
  const event = eventify(calendarEvent);

  const { start, end } = formatForGoogle(event.startUtc, event.endUtc, event.allDayEvent);

  const details: IGoogle = {
    action: 'TEMPLATE',
    text: event.title,
    details: `${event.description}\n\n${calendarEvent.url}`,
    location: event.location,
    trp: event.busy,
    dates: `${start}/${end}`,
  };

  if (!event.location || event.location === 'Underline') {
    delete details.location;
  }

  if (event.guests && event.guests.length) {
    details.add = event.guests.join();
  }

  return `https://calendar.google.com/calendar/render?${new URLSearchParams(details as Record<string, string>)}`;
};

export const outlook = (calendarEvent: ICalendarEvent): string => {
  const event = eventify(calendarEvent);
  const details: IOutlook = {
    path: '/calendar/action/compose',
    rru: 'addevent',
    startdt: event.startUtc,
    enddt: event.endUtc,
    subject: event.title,
    body: event.description,
    location: event.location,
    allday: event.allDayEvent || false,
  };

  return `https://outlook.live.com/calendar/0/deeplink/compose?${new URLSearchParams(
    details as Record<string, string>
  )}`;
};

export const office365 = (calendarEvent: ICalendarEvent): string => {
  const event = eventify(calendarEvent);
  const details: IOutlook = {
    path: '/calendar/action/compose',
    rru: 'addevent',
    startdt: event.startUtc,
    enddt: event.endUtc,
    subject: event.title,
    body: event.description,
    location: event.location,
    allday: event.allDayEvent || false,
  };

  return `https://outlook.office.com/calendar/0/action/compose?${new URLSearchParams(
    details as Record<string, string>
  )}`;
};

export const yahoo = (calendarEvent: ICalendarEvent): string => {
  const event = eventify(calendarEvent);
  const { start, end } = formatTimes(event, event.allDayEvent ? 'allDay' : 'dateTimeUTC');
  const details: IYahoo = {
    v: 60,
    title: event.title,
    st: start,
    et: end,
    desc: event.description,
    in_loc: event.location,
    dur: event.allDayEvent ? 'allday' : false,
  };

  return `https://calendar.yahoo.com/?${new URLSearchParams(details as Record<string, string>).toString()}`;
};

export const ics = (calendarEvent: ICalendarEvent): string => {
  const event = eventify(calendarEvent);
  const formattedDescription: string = (event.description || '')
    .replace(/,/gm, ',')
    .replace(/;/gm, ';')
    .replace(/\r\n/gm, '\n')
    .replace(/\n/gm, '\\n')
    .replace(/(\\n)[\s\t]+/gm, '\\n');

  const formattedLocation: string = (event.location || '')
    .replace(/,/gm, ',')
    .replace(/;/gm, ';')
    .replace(/\r\n/gm, '\n')
    .replace(/\n/gm, '\\n')
    .replace(/(\\n)[\s\t]+/gm, '\\n');

  const { start, end } = formatTimes(event, event.allDayEvent ? 'allDay' : 'dateTimeUTC');
  const calendarChunks = [
    {
      key: 'BEGIN',
      value: 'VCALENDAR',
    },
    {
      key: 'VERSION',
      value: '2.0',
    },
    {
      key: 'BEGIN',
      value: 'VEVENT',
    },
    {
      key: 'URL',
      value: event.url,
    },
    {
      key: 'DTSTART',
      value: start,
    },
    {
      key: 'DTEND',
      value: end,
    },
    {
      key: 'SUMMARY',
      value: event.title,
    },
    {
      key: 'DESCRIPTION',
      value: formattedDescription,
    },
    {
      key: 'LOCATION',
      value: formattedLocation,
    },
    {
      key: 'ORGANIZER',
      value: event.organizer,
    },
    {
      key: 'END',
      value: 'VEVENT',
    },
    {
      key: 'END',
      value: 'VCALENDAR',
    },
  ];

  let calendarUrl = '';

  calendarChunks.forEach((chunk) => {
    if (chunk.value) {
      if (chunk.key == 'ORGANIZER') {
        const value = chunk.value as ICalendarEventOrganizer;

        calendarUrl += `${chunk.key};${encodeURIComponent(`CN=${value.name}:MAILTO:${value.email}\n`)}`;
      } else {
        calendarUrl += `${chunk.key}:${encodeURIComponent(`${chunk.value}\n`)}`;
      }
    }
  });

  return `data:text/calendar;charset=utf8,${calendarUrl}`;
};

export type { ICalendarEvent };
